import {TimeSpan} from "@sparkware/app-utils";
import {makeObservable, observable, runInAction} from "mobx";
import {delay} from "../delay";
import {IAmountAnimationOptions, IAmountAnimator} from "./amount-animator.interface";

export class AmountAnimator implements IAmountAnimator {

    constructor(private readonly getTargetAmount: () => number) {
        makeObservable(this);
        this.currentValue = getTargetAmount();
    }

    @observable.ref
    private _currentValue: number;
    get currentValue(): number {
        return this._currentValue;
    }

    private set currentValue(value: number) {
        runInAction(() => {
            this._currentValue = value;
        })
    }

    private _isStarted: boolean = false;
    stopAnimation(): void {
        this._isStarted = false;
    }

    private _currentAnimationPromise: Promise<void>  =  Promise.resolve();
    startAnimation(options: IAmountAnimationOptions): Promise<void> {
        options = {
            fromAmount: 0,
            ...options
        };

        if(this._isStarted) {
            this.stopAnimation();
        }

        // in case there was an animation in progress that was stopped above then we first wait for that one to completely stop before starting a new one
        this._currentAnimationPromise = this._currentAnimationPromise.finally(async () => {
            this._isStarted = true;
            try {
                await this._executeAnimation(options)
            } finally {
                this._isStarted = false;
            }
        });

        return this._currentAnimationPromise;
    }


    private async _executeAnimation(options: IAmountAnimationOptions): Promise<void> {

        const from = options.fromAmount;
        const to = this.getTargetAmount();


        const diff = to - from;
        if(diff <= 0) {
            this.currentValue = to;
            return;
        }

        this.currentValue = from;

        const iterations = options.animationDuration.totalMilliseconds / options.iterationDuration.totalMilliseconds;
        const iterationIncrement = diff / iterations;


        for(let i = 1; i <= iterations; i++) {
            if(!this._isStarted) {
                break;
            }
            this.currentValue = this.currentValue + iterationIncrement;

            await delay(TimeSpan.fromMilliseconds(options.iterationDuration.totalMilliseconds));
        }

        this.currentValue = this.getTargetAmount();
    }
}